/**
 * Crosstab Visual Modes用の配色スキーム定義
 */

export type ColorSchemeName = 'blue' | 'red' | 'green' | 'diverging';

export interface ColorScheme {
  name: ColorSchemeName;
  displayName: string;
  type: 'sequential' | 'diverging';
  colors: {
    min: string; // 最小値の色
    max: string; // 最大値の色
    mid?: string; // Divergingの場合の中央値の色
    null: string; // NULL値の色
  };
}

/**
 * 利用可能な配色スキーム
 */
export const COLOR_SCHEMES: Record<ColorSchemeName, ColorScheme> = {
  blue: {
    name: 'blue',
    displayName: 'Blue',
    type: 'sequential',
    colors: {
      min: '#E3F2FD', // 薄い青
      max: '#0D47A1', // 濃い青
      null: '#F5F5F5' // グレー
    }
  },
  red: {
    name: 'red',
    displayName: 'Red',
    type: 'sequential',
    colors: {
      min: '#FFEBEE', // 薄い赤
      max: '#B71C1C', // 濃い赤
      null: '#F5F5F5'
    }
  },
  green: {
    name: 'green',
    displayName: 'Green',
    type: 'sequential',
    colors: {
      min: '#E8F5E9', // 薄い緑
      max: '#1B5E20', // 濃い緑
      null: '#F5F5F5'
    }
  },
  diverging: {
    name: 'diverging',
    displayName: 'Diverging (Red-White-Blue)',
    type: 'diverging',
    colors: {
      min: '#B71C1C', // 濃い赤（最小値）
      mid: '#FFFFFF', // 白（中央値）
      max: '#0D47A1', // 濃い青（最大値）
      null: '#F5F5F5'
    }
  }
};

/**
 * RGB値を分解
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    throw new Error(`Invalid hex color: ${hex}`);
  }
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  };
}

/**
 * RGB値を16進数に変換
 */
function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => {
    const hex = Math.round(n).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * 2つの色の間を線形補間
 */
function interpolateColor(color1: string, color2: string, ratio: number): string {
  const c1 = hexToRgb(color1);
  const c2 = hexToRgb(color2);

  const r = c1.r + (c2.r - c1.r) * ratio;
  const g = c1.g + (c2.g - c1.g) * ratio;
  const b = c1.b + (c2.b - c1.b) * ratio;

  return rgbToHex(r, g, b);
}

/**
 * 値を色にマッピング（Sequential配色）
 *
 * @param value 値
 * @param min 最小値
 * @param max 最大値
 * @param scheme 配色スキーム
 * @returns 色（16進数）
 */
export function mapValueToColorSequential(
  value: number,
  min: number,
  max: number,
  scheme: ColorScheme
): string {
  if (min === max) {
    // 全ての値が同じ場合は中間色
    return interpolateColor(scheme.colors.min, scheme.colors.max, 0.5);
  }

  // 正規化（0-1の範囲に）
  const ratio = (value - min) / (max - min);

  // 線形補間
  return interpolateColor(scheme.colors.min, scheme.colors.max, ratio);
}

/**
 * 値を色にマッピング（Diverging配色）
 *
 * @param value 値
 * @param min 最小値
 * @param max 最大値
 * @param median 中央値
 * @param scheme 配色スキーム
 * @returns 色（16進数）
 */
export function mapValueToColorDiverging(
  value: number,
  min: number,
  max: number,
  median: number,
  scheme: ColorScheme
): string {
  if (!scheme.colors.mid) {
    throw new Error('Diverging scheme requires mid color');
  }

  if (value < median) {
    // 中央値未満: 最小値から中央値へ補間
    if (min === median) {
      return scheme.colors.mid;
    }
    const ratio = (value - min) / (median - min);
    return interpolateColor(scheme.colors.min, scheme.colors.mid, ratio);
  } else {
    // 中央値以上: 中央値から最大値へ補間
    if (median === max) {
      return scheme.colors.mid;
    }
    const ratio = (value - median) / (max - median);
    return interpolateColor(scheme.colors.mid, scheme.colors.max, ratio);
  }
}

/**
 * 値を色にマッピング（配色スキームのタイプに応じて自動選択）
 */
export function mapValueToColor(
  value: number | null,
  min: number,
  max: number,
  median: number,
  scheme: ColorScheme
): string {
  // NULL値
  if (value === null) {
    return scheme.colors.null;
  }

  // Diverging
  if (scheme.type === 'diverging') {
    return mapValueToColorDiverging(value, min, max, median, scheme);
  }

  // Sequential
  return mapValueToColorSequential(value, min, max, scheme);
}

/**
 * 背景色に対して適切な文字色を返す（コントラスト調整）
 *
 * @param backgroundColor 背景色（16進数）
 * @returns 文字色（'#000000' または '#FFFFFF'）
 */
export function getContrastTextColor(backgroundColor: string): string {
  const rgb = hexToRgb(backgroundColor);

  // 相対輝度の計算（WCAG準拠）
  const luminance =
    (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;

  // 輝度が0.5以上なら黒文字、未満なら白文字
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

/**
 * 統計値を計算
 */
export function calculateStatistics(values: number[]): {
  min: number;
  max: number;
  median: number;
} {
  if (values.length === 0) {
    return { min: 0, max: 0, median: 0 };
  }

  const sorted = [...values].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];

  // 中央値
  const mid = Math.floor(sorted.length / 2);
  const median =
    sorted.length % 2 === 0
      ? (sorted[mid - 1] + sorted[mid]) / 2
      : sorted[mid];

  return { min, max, median };
}
